// import lib(s)
import React, { useEffect, useMemo, useState, useRef, useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"
import styles from "./MedianBeats.module.css"
import { useTheme } from "@emotion/react"

import ROUTES from "../../../route/routes"
import { useNavigate, useParams } from "react-router-dom"

import { useAxiosPrivate, useDocumentTitle } from "../../../hooks"
import { PAGE_TITLE } from "../../../constants/constants"

import Layout from "../../../layouts/common/Layout"
import PlaceholderText from "../../../components/common/SampleText"
import Loader from "../../../components/common/Loader"
import toast from "react-hot-toast"

import Tabs from "@mui/material/Tabs"
import Tab from "@mui/material/Tab"
import Box from "@mui/material/Box"
import Card from "@mui/material/Card"
import CardContent from "@mui/material/CardContent"
import Typography from "@mui/material/Typography"
import Button from "@mui/material/Button"
import Autocomplete from "@mui/material/Autocomplete"
import Divider from "@mui/material/Divider"
import TextField from "@mui/material/TextField"
import FormLabel from "@mui/material/FormLabel"
import FormControl from "@mui/material/FormControl"
import FormGroup from "@mui/material/FormGroup"
import FormControlLabel from "@mui/material/FormControlLabel"
import FormHelperText from "@mui/material/FormHelperText"
import Checkbox from "@mui/material/Checkbox"
import Switch from "@mui/material/Switch"
import Slider from "@mui/material/Slider"
import Link from "@mui/material/Link"
import List from "@mui/material/List"
import ListItem from "@mui/material/ListItem"
import ListItemButton from "@mui/material/ListItemButton"
import ListItemIcon from "@mui/material/ListItemIcon"
import ListItemText from "@mui/material/ListItemText"
import { Grid } from "@mui/material"

import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { selectLead, addFiducialPoint, updateFiducialPointContext, updateGhostFiducialPoint, removeFiducialPoint, selectCurrentFiducialPoints, selectCurrentBeatType, selectCurrentMedianBeatWindow } from '../../Annotator/AnnotatorSlice'
import { FIDUCIAL_POINT_TYPES, MEDIAN_BEAT_ZOOM } from '../../../constants/annotator'
import FiducialPointContextMenu from '../../Annotator/components/FiducialPointContextMenu/FiducialPointContextMenu'

import ECGGrid from "../../../components/Grid"
import FullWidthECGPreview from "../../../components/FullWidthECGPreview"

import applyInversion from '../../../utils/applyInversion'
import { getMedianBeatFrequency, getMedianBeatRPeakSample, msToSample, sampleToMs } from '../../../utils/fiducialIntervals'
import { computeRmsWaveform } from '../../../utils/rmsWaveform'
import { computeMedianBeatWaveforms } from '../../../utils/medianBeatCompute'
import { resolveAnnotationFeatures } from '../../../utils/annotationFeatures'
import * as Lib from '../../../constants/annotator'

// Canonical 12-lead ordering. Used to sort the legend rows so V-leads follow
// limb leads in the standard clinical sequence, regardless of the order they
// appear in the sample dict (which mixes measured vs. synthesized V leads).
const LEAD_ORDER = ["I", "II", "III", "aVR", "aVL", "aVF", "V1", "V2", "V3", "V4", "V5", "V6"]
const leadSortKey = (leadName) => {
	const base = leadName.replace(/-syn$/, "")
	const idx = LEAD_ORDER.indexOf(base)
	return idx === -1 ? LEAD_ORDER.length : idx
}

// Sentinel key used to inject the RMS composite tile at the bottom of the
// per-lead median-beat grid.
const RMS_KEY = '__rms__'

// Palette used to color per-lead waveforms when tiles are collapsed (vertical
// spacing < 100%), so the annotator can distinguish overlapping traces. Picked
// to be distinguishable on a light grid background.
const LEAD_PALETTE = [
	"#bb2739", // default red — lead 1 keeps the familiar color
	"#1f77b4", // blue
	"#2ca02c", // green
	"#ff7f0e", // orange
	"#9467bd", // purple
	"#8c564b", // brown
	"#e377c2", // pink
	"#17becf", // cyan
	"#bcbd22", // olive
	"#7f7f7f", // gray
	"#393b79", // navy
	"#637939", // dark green
]

const MedianBeats = () => {
	const dispatch = useDispatch()
	const navigate = useNavigate()
	const urlParams = useParams()
	const theme = useTheme()
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)

	const boxes = useRef([]);
	const [scrollPosition, setScrollPosition] = useState(0);
	const [LeadGroup, setLeadGroup] = useState([]);

	// Annotator Data
	const AnnotatorData = useSelector((state) => state.Annotator)
	const { medianBeatFiducialPointAnnotation: medianBeatFiducialPointAnnotationEnabled } = resolveAnnotationFeatures(AnnotatorData.annotations)
	const { rmsView: rmsViewEnabled } = resolveAnnotationFeatures(AnnotatorData.annotations)
	const AnnotatorSettings = useSelector((state) => state.Annotator.settings)
	const Filters = useSelector((state) => state.Annotator.filtered.filters)
	const currentFiducialPoints = useSelector(selectCurrentFiducialPoints)
	const currentBeatType = useSelector(selectCurrentBeatType)
	const currentWindow = useSelector(selectCurrentMedianBeatWindow)
	const ecg = AnnotatorData.filtered
	const totalLeads = AnnotatorData.leadCount
	const inverted = AnnotatorSettings.inverted
	const samples = useMemo(
		() => (inverted ? applyInversion(ecg.samples) : ecg.samples),
		[inverted, ecg.samples],
	)

	const [ThreeRowSamples, setThreeRowSamples] = useState({
		I: [],
		II: [],
		III: [],
		aVR: [],
		aVL: [],
		aVF: [],
		"V1-syn": [],
		"V2-syn": [],
		V1: [],
		V2: [],
		"V3-syn": [],
		V4: [],
		"V5-syn": [],
		"V6-syn": [],
	});
	const [ThreeRowSampleDuration, setThreeRowSampleDuration] = useState(2.5);
	const [Frequency, setFrequency] = useState(ecg.frequency);
	const [WindowCalc, setWindowCalc] = useState({windowWidthPerColumn: 0, samplePerPx: 0, samplesPerColumn: 0, totalSamples:0, samplesPerWindow: 0, newDuration: 0});
	// Session-only: which leads the user has hidden via the legend toggles in
	// collapsed single-column mode. Not persisted; resets on remount.
	const [hiddenLeads, setHiddenLeads] = useState(() => new Set())
	const toggleLeadVisibility = useCallback((leadName) => {
		setHiddenLeads(prev => {
			const next = new Set(prev)
			if (next.has(leadName)) next.delete(leadName)
			else next.add(leadName)
			return next
		})
	}, [])

	const handleBoxScroll = useCallback((e, index) => {
		const scrollLeft = e.target.scrollLeft;
		setScrollPosition(scrollLeft);

		boxes.current.forEach((box, i) => {
		if (i !== index) {
			box.scrollLeft = scrollLeft;
		}
		});
	}, []);

	const handleFiducialPointRightClick = useCallback((e, sampleIndex) => {
		if (!medianBeatFiducialPointAnnotationEnabled) return
		const existingPoints = currentFiducialPoints
		const freq = getMedianBeatFrequency(AnnotatorData)
		const waveformLen = ThreeRowSamples?.II?.length || Object.values(ThreeRowSamples || {})[0]?.length || 0
		const rPeak = getMedianBeatRPeakSample(AnnotatorData, waveformLen)
		// Check if click is near an existing point (within 5 samples tolerance)
		const nearbyPoint = existingPoints.find(fp => {
			const fpSample = msToSample(fp.offsetMs, freq, rPeak)
			return Math.abs(fpSample - sampleIndex) <= 5
		})
		dispatch(updateFiducialPointContext({
			show: true,
			data: {
				top: e.clientY,
				left: e.clientX,
				sampleIndex: sampleIndex,
				offsetMs: sampleToMs(sampleIndex, freq, rPeak),
				existingType: nearbyPoint?.type || null,
			}
		}))
	}, [dispatch, AnnotatorData, ThreeRowSamples, currentFiducialPoints, medianBeatFiducialPointAnnotationEnabled])

	const handleFiducialPointDrag = useCallback((type, sampleIndex) => {
		if (!medianBeatFiducialPointAnnotationEnabled) return
		const freq = getMedianBeatFrequency(AnnotatorData)
		const waveformLen = ThreeRowSamples?.II?.length || Object.values(ThreeRowSamples || {})[0]?.length || 0
		const rPeak = getMedianBeatRPeakSample(AnnotatorData, waveformLen)
		dispatch(addFiducialPoint({ type, offsetMs: sampleToMs(sampleIndex, freq, rPeak) }))
	}, [dispatch, AnnotatorData, ThreeRowSamples, medianBeatFiducialPointAnnotationEnabled])

	
	const effectiveScale = AnnotatorData.settings.scale * MEDIAN_BEAT_ZOOM
	// Uniform strip height so median-beat waveforms render at the same
	// amplitude scaling in single-column, multi-column, and row views
	// (all use heightFactor=2 → 0.5 mV per big grid square).
	const HeightPerStrip = 200
	const AllLeadWidth = (10*100) * AnnotatorData.settings.scale
	const rhythmLead = AnnotatorData.selectedLead || "II"
	const rhythmSample = samples[rhythmLead]
	const rhythmDuration = rhythmSample?.length / ecg.frequency || ecg.duration || 10
	const rhythmWidth = rhythmDuration * Lib.PX_PER_SECOND * effectiveScale

	// Translate stored ms-relative points into sample-space for the preview.
	const renderedFiducialPoints = useMemo(() => {
		const freq = getMedianBeatFrequency(AnnotatorData)
		const waveformLen = ThreeRowSamples?.II?.length || Object.values(ThreeRowSamples || {})[0]?.length || 0
		const rPeak = getMedianBeatRPeakSample(AnnotatorData, waveformLen)
		return currentFiducialPoints.map(fp => ({
			type: fp.type,
			sample: msToSample(fp.offsetMs, freq, rPeak),
		}))
	}, [AnnotatorData, ThreeRowSamples, currentFiducialPoints])

	// RMS median-beat trace: sqrt(mean(lead_k[i]^2)) across all leads.
	// computeRmsWaveform tolerates empty/typed-array/plain-array leads.
	const rmsWaveform = useMemo(() => computeRmsWaveform(ThreeRowSamples), [ThreeRowSamples])

	useEffect(() => {
		const baseLeadKeys = Object.keys(samples)
		// Append the RMS composite tile at the end so it lands at the bottom
		// of the last column without disrupting limb/precordial ordering.
		const leadKeys = rmsViewEnabled ? [...baseLeadKeys, RMS_KEY] : baseLeadKeys
		const chunkSize = AnnotatorSettings.medianBeatSingleColumn ? Math.max(leadKeys.length, 1) : 3
		const leadGroups = leadKeys.reduce((resultArray, item, index) => {
			const chunkIndex = Math.floor(index/chunkSize)
			if(!resultArray[chunkIndex]) {
				resultArray[chunkIndex] = []
			}
			resultArray[chunkIndex].push(item)
			return resultArray
		}, [])
		setLeadGroup(leadGroups)

		const windowWidthPerColumn = 1000/leadGroups.length
		// ecg.frequency number of samples in 100px
		const samplePerPx = ecg.frequency/100
		const samplesPerColumn = samplePerPx*windowWidthPerColumn
		const totalSamples  = ecg.frequency*10
		const samplesPerWindow = (leadGroups.length) * samplesPerColumn
		let newDuration
		// let newDuration = (totalSamples - samplesPerWindow + samplesPerColumn) / ecg.frequency

		// -- console.log("Total samples: ", totalSamples);
		// -- console.log("Window Size: ", samplesPerWindow);
		// -- console.log("New Duration: ", newDuration);

		// Leads of leadGroups[0] will from 0 index to samplesPerColumn*leadGroups.length
		// Leads of leadGroups[1] will from samplesPerColumn to samplesPerColumn*(leadGroups.length - 1)

		if (totalLeads == 12) {
			const computed = computeMedianBeatWaveforms(AnnotatorData, { requireComputable: false })
			if (computed) {
				setFrequency(computed.renderRate)
				setThreeRowSamples(computed.waveforms)
			} else {
				setThreeRowSamples({})
			}
			newDuration=2.5;
		} else {
			const computed = computeMedianBeatWaveforms(AnnotatorData, { slug: currentBeatType, requireComputable: false })
			const output = computed?.waveforms || {}
			const renderRate = computed?.renderRate || AnnotatorData.filtered.frequency
			setThreeRowSamples({ ...output })
			newDuration=10;
			setFrequency(renderRate)
			// Prune any fiducial points that no longer fall within the recomputed window.
			// Gate on the window-loaded marker: on initial load the settings briefly hold
			// the slice defaults (600/600) before RightPanel dispatches the saved values,
			// and pruning during that interval would delete any point outside ±600ms even
			// if the saved window would have kept it. Once the marker matches the current
			// recording, the window is authoritative and pruning is safe.
			if (medianBeatFiducialPointAnnotationEnabled && AnnotatorData.medianBeatWindowLoadedForRecordingId === AnnotatorData.currentRecordingId) {
				const leftMs = currentWindow.leftMs
				const rightMs = currentWindow.rightMs
				const existingPoints = currentFiducialPoints
				existingPoints.forEach(fp => {
					if (fp.offsetMs < -leftMs || fp.offsetMs > rightMs) {
						dispatch(removeFiducialPoint(fp.type))
					}
				})
			}
		}

		setWindowCalc({windowWidthPerColumn, samplePerPx, samplesPerColumn, totalSamples, samplesPerWindow, newDuration})
		setThreeRowSampleDuration(newDuration)
		setLoading(false)
	}, [samples, AnnotatorSettings, AnnotatorData.annotations.beats, currentBeatType, medianBeatFiducialPointAnnotationEnabled, rmsViewEnabled])
	


	return (
		<>
		{loading ? (
			<Loader />
		) : error ? (
			<PlaceholderText>Error</PlaceholderText>
		) : (
			<React.Fragment>
			{(() => {
				const maxLeadsPerColumn = LeadGroup.reduce((m, g) => Math.max(m, g.length), 0)
				const perLeadHeightPx = HeightPerStrip * effectiveScale
				const spacing = (AnnotatorSettings.medianBeatSingleColumn && totalLeads > 1)
					? AnnotatorSettings.medianBeatVerticalSpacing
					: 1
				// In single-column mode, tighter spacing collapses per-lead tiles
				// upward (negative marginTop). Compensate the outer column height
				// so we don't leave empty space below the collapsed stack. The RMS
				// tile at the bottom keeps its full height, so subtract overlap
				// only from the per-lead tiles above it.
				const perLeadTilesInColumn = AnnotatorSettings.medianBeatSingleColumn
					? Math.max(0, maxLeadsPerColumn - (rmsViewEnabled ? 1 : 0))
					: 0
				const totalOverlapPx = Math.max(0, perLeadTilesInColumn - 1) * (1 - spacing) * perLeadHeightPx
				const columnHeightPx = perLeadHeightPx * maxLeadsPerColumn - totalOverlapPx
				const hideLeadLabels = AnnotatorSettings.medianBeatSingleColumn && spacing < 1
				// All tiles expose the panel's shared paper, which stays fixed as
				// single-column tiles overlap and continues beyond the plot edges.
				const useSharedGrid = AnnotatorSettings.medianBeatSingleColumn
				// Plot geometry uses the same effective scale as the panel's paper.
				const sharedGridWidthPx = (ThreeRowSampleDuration || 2.5) * Lib.PX_PER_SECOND * effectiveScale
				// The waveform itself may occupy less than the full grid width — for
				// 1/6-lead the SVG viewport is 10s wide but the median-beat window
				// (e.g. ±600ms) trims the sample count. Compute the actual x-extent
				// of the rendered waveform so we can anchor the legend just past it.
				const anyLeadSample = ThreeRowSamples ? Object.values(ThreeRowSamples).find(a => a && a.length > 0) : null
				const waveformSampleCount = anyLeadSample ? anyLeadSample.length : 0
				const waveformWidthPx = Frequency > 0
					? (waveformSampleCount / Frequency) * Lib.PX_PER_SECOND * effectiveScale
					: sharedGridWidthPx
				// When the legend is shown (single-column + collapsed), extend the
				// column wrapper's width to host the legend just to the right of
				// the actual waveform. Compute the legend's expected width from
				// its content — 3 rows per column, each row = swatch + gap + label.
				const legendLeadCount = hideLeadLabels
					? (LeadGroup[0] || []).filter(l => l !== RMS_KEY).length
					: 0
				const legendNumCols = Math.max(1, Math.ceil(legendLeadCount / 3))
				// Per-column width: swatch (5) + gap (3) + longest label (~4 chars * 0.6em)
				// + column gap (6), all * effectiveScale, in px. "V6-syn" is the widest
				// lead name at ~6 chars, so use 6 chars as the upper bound.
				const legendColContentPx = (5 + 3 + 6 * 6 * 0.6) * effectiveScale
				const legendColGapPx = 6 * effectiveScale
				const legendPaddingPx = 6 * effectiveScale
				const legendAreaWidthPx = hideLeadLabels
					? legendNumCols * legendColContentPx + Math.max(0, legendNumCols - 1) * legendColGapPx + 2 * legendPaddingPx + 8 * effectiveScale
					: 0
				const columnWidthPx = useSharedGrid
					? waveformWidthPx + legendAreaWidthPx
					: AllLeadWidth
				return (
			<div className={styles.Container} style={{height: `${columnHeightPx}px`, width: `${AllLeadWidth}px`}}>
				{LeadGroup.map((leadGroup, indexL) => (
					<div
						key={leadGroup}
						style={{ width: `${100 / LeadGroup.length}%`, overflowX: "hidden"}}
						className={styles.box}
						ref={(el) => (boxes.current[indexL] = el)}
						onScroll={(e) => handleBoxScroll(e, indexL)}
					>
						<div style={{ height: `${columnHeightPx}px`, width: `${columnWidthPx}px`, position: "relative", background: "transparent", overflow: "hidden" }}>
							{hideLeadLabels && (() => {
								// Legend: color swatch + lead name for each non-RMS tile in
								// this column, sorted into canonical 12-lead order. Laid out
								// as 3 leads per column so tall lead lists stay compact.
								// Each lead is one inline-flex row so the swatch and label
								// stay aligned; the outer grid arranges those rows.
								const perLeadLeads = leadGroup
									.filter(l => l !== RMS_KEY)
									.slice()
									.sort((a, b) => leadSortKey(a) - leadSortKey(b))
								if (perLeadLeads.length === 0) return null
								const fontSize = 6 * effectiveScale
								const swatchSize = 5 * effectiveScale
								const rowsPerCol = 3
								const numCols = Math.max(1, Math.ceil(perLeadLeads.length / rowsPerCol))
								return (
									<div
										style={{
											position: "absolute",
											top: `${3 * effectiveScale}px`,
											left: `${waveformWidthPx + 6 * effectiveScale}px`,
											background: "rgba(255,255,255,0.9)",
											border: "1px solid #ccc",
											borderRadius: `${2 * effectiveScale}px`,
											padding: `${2 * effectiveScale}px ${3 * effectiveScale}px`,
											display: "grid",
											gridAutoFlow: "column",
											gridTemplateRows: `repeat(${rowsPerCol}, auto)`,
											gridTemplateColumns: `repeat(${numCols}, auto)`,
											columnGap: `${6 * effectiveScale}px`,
											rowGap: `${1 * effectiveScale}px`,
											fontSize: `${fontSize}px`,
											lineHeight: 1.1,
											fontFamily: "Work Sans, Roboto, Helvetica, Arial, sans-serif",
											color: "#000",
											zIndex: 2,
										}}
									>
										{perLeadLeads.map((leadName) => {
											const paletteIdx = leadSortKey(leadName)
											const color = LEAD_PALETTE[paletteIdx % LEAD_PALETTE.length]
											const isHidden = hiddenLeads.has(leadName)
											return (
												<div
													key={leadName}
													onClick={() => toggleLeadVisibility(leadName)}
													style={{
														display: "flex",
														alignItems: "center",
														gap: `${3 * effectiveScale}px`,
														whiteSpace: "nowrap",
														cursor: "pointer",
														userSelect: "none",
														opacity: isHidden ? 0.45 : 1,
													}}
													title={isHidden ? `Show ${leadName}` : `Hide ${leadName}`}
												>
													<span style={{
														display: "inline-block",
														width: `${swatchSize}px`,
														height: `${swatchSize}px`,
														background: isHidden ? "transparent" : color,
														border: `1px solid ${color}`,
														boxSizing: "border-box",
														flexShrink: 0,
													}} />
													<span style={{
														fontWeight: 600,
														textDecoration: isHidden ? "line-through" : "none",
													}}>{leadName}</span>
												</div>
											)
										})}
									</div>
								)
							})()}
							{leadGroup.map((lead, index) => {
								const isRms = lead === RMS_KEY
								const tileTitle = isRms ? "RMS" : (hideLeadLabels ? null : lead)
								// Hidden leads: keep the tile slot but render no waveform,
								// so the shared grid layout stays stable and clicks on the
								// legend toggle visibility on the fly.
								// The legend-driven hide only applies while the legend is
								// visible (single-column + collapsed). In multi-column
								// mode or at 100% spacing, all leads render regardless of
								// the persisted hiddenLeads set.
								const isLeadHidden = hideLeadLabels && !isRms && hiddenLeads.has(lead)
								const rawTileSample = isRms ? rmsWaveform : (isLeadHidden ? [] : ThreeRowSamples[lead])
								const tileSample = (!isRms && Filters.noSyn && lead.includes('syn'))
									? new Array((rawTileSample || []).length).fill(0)
									: rawTileSample
								const isFirstInGroup = index === 0
								const applyOverlap = AnnotatorSettings.medianBeatSingleColumn && !isRms && !isFirstInGroup && spacing < 1
								const marginTop = applyOverlap ? -(1 - spacing) * perLeadHeightPx : 0
								// Keep overlapping tiles transparent above the shared paper.
								const tileTransparent = AnnotatorSettings.medianBeatSingleColumn
								// When leads are collapsing, color each per-lead waveform
								// distinctly so overlapping traces can be told apart. Uses
								// the canonical 12-lead index so the color matches the
								// legend regardless of the sample-dict order. RMS keeps its
								// default color.
								let waveformColor = null
								if (hideLeadLabels && !isRms) {
									const paletteIdx = leadSortKey(lead)
									waveformColor = LEAD_PALETTE[paletteIdx % LEAD_PALETTE.length]
								}
								return (
								<div data-testid={`median-tile-${lead}`} className={tileTransparent ? styles.transparentTile : undefined} style={{height:`${perLeadHeightPx}px`,  "--ECGScale": effectiveScale, position: "relative", marginTop: `${marginTop}px`, background: "transparent", zIndex: 1, pointerEvents: isLeadHidden ? "none" : "auto"}} key={index}>
									<FullWidthECGPreview
										key={index}
										ecgTitle={tileTitle}
										enableBeats={false}
										scale={effectiveScale}
										sample={tileSample}
										seconds={ThreeRowSampleDuration || 2.5}
										heightFactor={2}
										mvSwing={ecg.gain * 2.5}
										onClick={isRms ? undefined : ()=>{dispatch(selectLead(lead))}}
										onRightClick={medianBeatFiducialPointAnnotationEnabled ? handleFiducialPointRightClick : undefined}
										onFiducialPointDrag={medianBeatFiducialPointAnnotationEnabled ? handleFiducialPointDrag : undefined}
										style={{cursor: "pointer"}}
										frequency={Frequency}
										offsetGrid={!(indexL%2 === 0) && WindowCalc.newDuration == 2.5}
										hideGridLines={true}
										waveformStrokeColor={waveformColor}
										fiducialPoints={renderedFiducialPoints}
										ghostFiducialPoint={medianBeatFiducialPointAnnotationEnabled && AnnotatorData.ghostFiducialPoint?.show ? { sampleIndex: AnnotatorData.ghostFiducialPoint.sampleIndex } : null}
										fiducialPointLabel={FIDUCIAL_POINT_TYPES}
									/>
								</div>
								)
							})}
						</div>
					</div>
				))}
			</div>
			);
			})()}
			{/* {ThreeRowVerticalSamples.map((sample, index) => (
				<div key={index} className={styles.fullLead}>
					<div style={{ height: `199px`, width: `${AllLeadWidth}px`, position: "relative" }}>
						<span style={{ position: "fixed", padding: "5px", fontWeight: "900", borderRadius: "4px", backgroundColor: "#ffffff66", color: "#000" }}>{AnnotatorData.selectedLead || "II"}</span>
						<FullWidthECGPreview
							enableBeats={true}
							sample={sample}
							beats={AnnotatorData.annotations.beats}
							seconds={10 || 30}
							heightFactor={2}
							mvSwing={ecg.gain * 2.5}
							frequency={ecg.frequency}
							// renderTimeScaleFactor = {2}	
						/>
					</div>
				</div>
			))} */}
			{samples["II"] && (
				<div role="region" aria-label="Median plot rhythm strip" style={{ overflowX: "auto", width: "100%", height: `${170 * effectiveScale}px`, flexShrink: 0, marginTop: `${24 * effectiveScale}px` }}>
					<div style={{ height: `${170 * effectiveScale}px`, width: `${rhythmWidth}px`, minWidth: "100%", position: "relative" }}>
						{/* <span style={{ position: "fixed", padding: "5px", fontWeight: "900", borderRadius: "4px", backgroundColor: "#ffffff66", color: "#000" }}>{AnnotatorData.selectedLead || "II"}</span> */}
						<FullWidthECGPreview
							enableBeats={true}
							ecgTitle = {rhythmLead}
							hideGridLines={true}
							fillWidth={true}
							sample={rhythmSample}
							scale = {effectiveScale}
							beats={AnnotatorData.annotations.beats}
							beatLabel = {AnnotatorData.annotations.labels.beats}
							seconds={rhythmDuration}
							heightFactor={1.7}
							mvSwing={ecg.gain * 2.5}
							frequency={ecg.frequency}
							renderTimeScaleFactor = {1}
						/>
					</div>
				</div>
			)}
			{medianBeatFiducialPointAnnotationEnabled && <FiducialPointContextMenu />}
			</React.Fragment>
		)}
	</>
)
}

export default MedianBeats
